/**
 * SessionExpired — pantalla que ve quien abre Galia (normalmente la app
 * instalada) con una sesión guardada que ya venció.
 *
 * Acuerdo con Santiago (reunión 01/09/2026): la sesión de la app instalada dura
 * 1 semana; después hay que volver a autenticarse. Sin esta pantalla, la app
 * caía a modo anónimo en silencio: el estudiante chateaba igual pero no quedaba
 * registrado en el dashboard, justo lo que Santiago no quería.
 *
 * Cómo se renueva: el estudiante entra a Galia desde Apolearn en el navegador.
 * Ese lanzamiento reescribe la sesión guardada (mismo origin que la app
 * instalada en Chrome/Edge escritorio y Android). Al volver aquí, la pantalla
 * detecta la sesión nueva (al recuperar el foco o con el botón) y recarga.
 *
 * Idioma: español para estudiantes; francés si el rol LTI de la sesión vencida
 * era Instructor (misma regla que el resto de la UI del modo profesor).
 */

import { useEffect, useState } from 'react';
import './SessionExpired.css';
import sessionService, { SessionIdentity } from '../../services/sessionService';
import { isDirectAccessAvailable } from '../../utils/featureFlags';

/** Portal de Apolearn de la AF; desde ahí el estudiante llega al módulo Galia. */
const APOLEARN_URL = 'https://afcolombie.apolearn.com/';

interface SessionExpiredProps {
  identity: SessionIdentity | null;
}

const TXT = {
  es: {
    title: 'Tu sesión venció',
    lead: (name: string) =>
      name
        ? `${name}, por seguridad Galia pide volver a entrar cada semana.`
        : 'Por seguridad, Galia pide volver a entrar cada semana.',
    steps: [
      'Entra a Apolearn y abre el módulo de Galia (si aparece « Abrir en pantalla completa », úsalo).',
      'Vuelve a esta ventana: Galia reconocerá tu sesión nueva y seguirá como siempre.',
    ],
    open: 'Ir a Apolearn',
    retry: 'Ya entré, continuar',
    notYet: 'Todavía no encontramos una sesión nueva. Entra por Apolearn y vuelve a intentar.',
    or: 'o',
    email: 'Entrar con mi correo',
    emailHint: 'Te enviamos un código de 6 dígitos al correo con el que estás inscrito en la Alianza Francesa.',
  },
  fr: {
    title: 'Votre session a expiré',
    lead: (name: string) =>
      name
        ? `${name}, par sécurité Galia demande de se reconnecter chaque semaine.`
        : 'Par sécurité, Galia demande de se reconnecter chaque semaine.',
    steps: [
      'Ouvrez Apolearn et lancez le module Galia (si « Ouvrir en plein écran » apparaît, utilisez-le).',
      'Revenez dans cette fenêtre : Galia reconnaîtra votre nouvelle session et reprendra normalement.',
    ],
    open: 'Aller sur Apolearn',
    retry: 'C’est fait, continuer',
    notYet: 'Aucune nouvelle session trouvée pour l’instant. Connectez-vous via Apolearn puis réessayez.',
    or: 'ou',
    email: 'Me connecter avec mon e-mail',
    emailHint: 'Nous vous envoyons un code à 6 chiffres à l’adresse enregistrée à l’Alliance Française.',
  },
};

/**
 * Salida por correo (Carlos, 18/09/2026): con una sesión de Apolearn vencida
 * guardada, la raíz del sitio NO muestra el acceso por correo
 * (isDirectAccessMode() respeta la sesión LTI guardada), así que quien quisiera
 * entrar con su correo quedaba atrapado aquí — le pasó a Carlos en su Chrome con
 * una sesión vieja de otra cuenta. `?directo=1` fuerza la puerta de correo; al
 * entrar, la sesión nueva reemplaza a la vencida (una sola clave en
 * localStorage) y esta pantalla no vuelve a salir.
 */
function goToEmailAccess() {
  const params = new URLSearchParams(window.location.search);
  // Estos parámetros mandan sobre la puerta de correo: se quitan.
  ['session', 'token', 'tache_id'].forEach((k) => params.delete(k));
  params.set('directo', '1');
  window.location.href = `${window.location.pathname}?${params.toString()}`;
}

function firstName(identity: SessionIdentity | null): string {
  const full = (identity?.full_name || '').trim();
  return full ? full.split(/\s+/)[0] : '';
}

export default function SessionExpired({ identity }: SessionExpiredProps) {
  const isTeacher = /instructor/i.test(identity?.roles || '');
  const t = isTeacher ? TXT.fr : TXT.es;
  const [notYet, setNotYet] = useState(false);

  // Si llegó una sesión nueva a localStorage, arrancar limpio con ella. La
  // recarga (y no un setState) garantiza que sessionService.resolve() y todo el
  // bootstrap de App.tsx corran otra vez con la identidad nueva.
  const tryContinue = () => {
    if (sessionService.hasFreshStoredSession()) {
      window.location.reload();
      return true;
    }
    return false;
  };

  // Al volver del navegador (foco / pestaña visible) se comprueba solo, sin que
  // el estudiante tenga que pulsar nada.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryContinue();
    };
    window.addEventListener('focus', tryContinue);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', tryContinue);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return (
    <div className="session-expired" role="dialog" aria-labelledby="session-expired-title">
      <div className="session-expired__card">
        <h1 id="session-expired-title" className="session-expired__title">{t.title}</h1>
        <p className="session-expired__lead">{t.lead(firstName(identity))}</p>
        <ol className="session-expired__steps">
          {t.steps.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
        <div className="session-expired__actions">
          <a
            className="session-expired__btn session-expired__btn--secondary"
            href={APOLEARN_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.open}
          </a>
          <button
            type="button"
            className="session-expired__btn"
            onClick={() => {
              if (!tryContinue()) setNotYet(true);
            }}
          >
            {t.retry}
          </button>
        </div>
        {notYet && <p className="session-expired__hint" role="status">{t.notYet}</p>}

        {isDirectAccessAvailable() && (
          <div className="session-expired__email">
            <span className="session-expired__or">{t.or}</span>
            <button
              type="button"
              className="session-expired__email-btn"
              onClick={goToEmailAccess}
            >
              {t.email}
            </button>
            <p className="session-expired__hint">{t.emailHint}</p>
          </div>
        )}
      </div>
    </div>
  );
}
